/**
 * AUROS — Impresión + Anillado Doble O
 * Archivo: assets/auros-anillado-automatico.js
 *
 * Funciones:
 * - Calcula hojas físicas y cantidad total de impresión.
 * - Selecciona automáticamente la variante de anillado.
 * - Permite elegir cuántos ejemplares llevarán anillado.
 * - Calcula subtotales y total estimado.
 * - Agrega impresión + anillado al carrito como líneas relacionadas.
 * - Respeta Kalles: VariantPicker, Printbox y wrapp-hdt-pr-frm.
 */

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-auros-anillado]';
  const MAXIMUM_PDF_SIZE = 20 * 1024 * 1024;

  const normalize = (value) =>
    String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  const positiveInteger = (value) => {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : 0;
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const extractNumber = (value) => {
    const match = String(value ?? '').match(/\d+/);
    return match ? Number(match[0]) : 0;
  };

  const localeRoot = () => window.Shopify?.routes?.root || '/';

  const createJobId = () => {
    const time = Date.now().toString(36).toUpperCase();

    if (window.crypto?.randomUUID) {
      return `AUR-${time}-${window.crypto.randomUUID().slice(0, 8).toUpperCase()}`;
    }

    return `AUR-${time}-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
  };

  const findOptionIndex = (options, tests, fallback = 0) => {
    const normalizedOptions = (options || []).map(normalize);

    for (let index = 0; index < normalizedOptions.length; index += 1) {
      if (tests.some((test) => test(normalizedOptions[index]))) {
        return index;
      }
    }

    return fallback;
  };

  const isDoubleSided = (value) => {
    const side = normalize(value);

    return (
      side.includes('ambas') ||
      side.includes('dos caras') ||
      side.includes('doble cara') ||
      side === '2 caras'
    );
  };

  const bindingCapacity = (sheets) => {
    if (sheets <= 0) return 0;
    if (sheets <= 50) return 50;
    if (sheets <= 60) return 60;
    if (sheets <= 80) return 80;
    if (sheets <= 120) return 120;
    if (sheets <= 180) return 180;
    if (sheets <= 240) return 240;
    return 0;
  };

  function init(root) {
    if (!(root instanceof Element)) return;
    if (root.dataset.aurosInitialized === 'true') return;

    const sectionId =
      root.dataset.sectionId ||
      (root.id?.startsWith('AurosAnillado-')
        ? root.id.replace('AurosAnillado-', '')
        : '');

    if (!sectionId) {
      console.warn('[AUROS Anillado] No se encontró sectionId.');
      return;
    }

    const configNode = document.getElementById(
      root.dataset.configId || `AurosAnilladoData-${sectionId}`
    );

    if (!configNode) {
      console.warn('[AUROS Anillado] No se encontró el JSON de configuración.');
      return;
    }

    let config;

    try {
      config = JSON.parse(configNode.textContent);
    } catch (error) {
      console.error('[AUROS Anillado] JSON inválido.', error);
      return;
    }

    const mainForm = document.getElementById(config.productFormId);

    if (!mainForm) {
      console.warn(
        '[AUROS Anillado] No se encontró el formulario principal:',
        config.productFormId
      );
      return;
    }

    const section =
      document.getElementById(`shopify-section-${config.sectionId}`) ||
      root.closest('.shopify-section') ||
      document;

    const stickyForm =
      (config.stickyFormId && document.getElementById(config.stickyFormId)) ||
      section.querySelector?.('.hdt-sticky-atc__form') ||
      null;

    const pagesInput = root.querySelector('[data-auros-pages]');
    const copiesInput = root.querySelector('[data-auros-copies]');
    const bindingChoices = [...root.querySelectorAll('[data-auros-binding-choice]')];

    const bindingQtyWrap = root.querySelector('[data-auros-binding-qty-wrap]');
    const bindingQtyInput = root.querySelector('[data-auros-binding-qty]');
    const bindingQtyHelp = root.querySelector('[data-auros-binding-qty-help]');
    const bindingMinus = root.querySelector('[data-auros-binding-minus]');
    const bindingPlus = root.querySelector('[data-auros-binding-plus]');

    const summary = root.querySelector('[data-auros-summary]');
    const summarySize = root.querySelector('[data-auros-summary-size]');
    const summarySides = root.querySelector('[data-auros-summary-sides]');
    const summarySheets = root.querySelector('[data-auros-summary-sheets]');
    const summaryPrintQty = root.querySelector('[data-auros-summary-print-qty]');

    const bindingSummary = root.querySelector('[data-auros-binding-summary]');
    const bindingTitle = root.querySelector('[data-auros-binding-title]');
    const bindingDetail = root.querySelector('[data-auros-binding-detail]');
    const bindingSummarySubtotal = root.querySelector('[data-auros-binding-subtotal]');

    const printSubtotalEl = root.querySelector('[data-auros-print-subtotal]');
    const bindingTotalRow = root.querySelector('[data-auros-binding-total-row]');
    const bindingTotalEl = root.querySelector('[data-auros-binding-total]');
    const grandTotalEl = root.querySelector('[data-auros-grand-total]');

    const errorBox = root.querySelector('[data-auros-error]');
    const successBox = root.querySelector('[data-auros-success]');

    const getDeferredPdfInput = () => {
      const namedControl = mainForm.elements?.namedItem(
        'properties[Archivo PDF para imprimir]'
      );

      if (
        namedControl instanceof HTMLInputElement &&
        namedControl.type === 'file' &&
        namedControl.dataset.aurosPdfDeferred === 'true'
      ) {
        return namedControl;
      }

      return mainForm.querySelector(
        'input[type="file"][data-auros-pdf-deferred="true"]'
      );
    };

    const getDeferredPdfFile = () => {
      const input = getDeferredPdfInput();
      return input?.files?.[0] || null;
    };

    const isPdfFile = (file) => {
      if (!file) return false;

      const hasPdfExtension = /\.pdf$/i.test(file.name || '');
      const hasPdfMime = !file.type || file.type === 'application/pdf';

      return hasPdfExtension && hasPdfMime;
    };

    // Verifica el PDF antes de utilizar las páginas para calcular el precio.
    let pdfVerificationVersion = 0;
    let pdfVerification = null;

    const verifyPdfPages = (file) => {
      if (pdfVerification?.file === file) return pdfVerification.promise;

      const version = ++pdfVerificationVersion;
      const promise = (async () => {
        const countPages = window.AurosPdfPageCounter?.countPages;
        if (typeof countPages !== 'function') {
          throw new Error('No se pudo iniciar el lector PDF. Recargue la página.');
        }

        const pages = await countPages(file);
        if (version !== pdfVerificationVersion || getDeferredPdfFile() !== file) {
          throw new Error('El archivo cambió durante la lectura. Seleccione el PDF nuevamente.');
        }
        if (!Number.isSafeInteger(pages) || pages < 1) {
          throw new Error('El PDF no contiene un número válido de páginas.');
        }

        pagesInput.value = String(pages);
        renderSummary();
        return pages;
      })();

      pdfVerification = { file, promise };
      void promise.catch(() => {
        if (pdfVerification?.promise === promise) pdfVerification = null;
      });
      return promise;
    };

    const onPdfFileChange = (event) => {
      if (event.target !== getDeferredPdfInput()) return;
      pdfVerificationVersion += 1;
      pdfVerification = null;
      pagesInput.value = '';
      renderSummary();

      const file = getDeferredPdfFile();
      if (!file) return;
      void verifyPdfPages(file).catch((error) => {
        if (getDeferredPdfFile() === file && !busy) {
          showError(error?.message || 'No se pudieron leer las páginas del PDF.');
        }
      });
    };

    /*
     * Precio nativo del producto.
     * Kalles lo actualiza con variant:change; AUROS lo vuelve a pintar
     * después con el total de la configuración cuando ya existen páginas
     * y ejemplares.
     */
    const nativePrice = section.querySelector?.(
      `hdt-price[form="${config.productFormId}"]`
    ) || null;

    const stickyVariantButton = section.querySelector?.(
      'hdt-sticky-btn-atc [tag-select]'
    ) || null;

    if (!pagesInput || !copiesInput || !bindingQtyInput) {
      console.warn('[AUROS Anillado] Faltan campos del configurador.');
      return;
    }

    root.dataset.aurosInitialized = 'true';

    const currency =
      config.currency ||
      window.Shopify?.currency?.active ||
      'COP';

    const formatMoney = (amountInMinorUnits) => {
      const value = Number(amountInMinorUnits) || 0;

      try {
        return new Intl.NumberFormat(
          document.documentElement.lang || 'es-CO',
          {
            style: 'currency',
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }
        ).format(value / 100);
      } catch {
        return `$${Math.round(value / 100).toLocaleString('es-CO')}`;
      }
    };

    const paintNativePrice = (state) => {
      if (!state?.variant) return;

      const hasCompleteConfiguration =
        state.pages > 0 &&
        state.copies > 0 &&
        state.printQuantity > 0;

      const amount = hasCompleteConfiguration
        ? state.grandTotal
        : Number(state.variant.price || 0);

      const formatted = hasCompleteConfiguration
        ? formatMoney(amount)
        : (state.variant.priceFormatted || formatMoney(amount));

      const money = nativePrice?.querySelector('.hdt-money');

      if (money) {
        money.textContent = formatted;
        nativePrice.dataset.aurosPriceMode = hasCompleteConfiguration
          ? 'configuration-total'
          : 'variant-price';

        nativePrice.setAttribute(
          'title',
          hasCompleteConfiguration
            ? 'Total estimado de la configuración seleccionada'
            : 'Precio de la variante seleccionada'
        );
      }

      /*
       * El sticky de Kalles muestra el precio como el segundo span del
       * botón de variante. Lo sincronizamos sin modificar su estructura.
       */
      if (stickyVariantButton) {
        const spans = stickyVariantButton.querySelectorAll('span');

        if (spans.length > 1) {
          spans[1].textContent = ` - ${formatted}`;
        }
      }
    };

    const mainSizeIndex = findOptionIndex(
      config.mainOptions,
      [
        (name) => name.includes('tamano') && name.includes('papel'),
        (name) => name === 'tamano',
        (name) => name.includes('papel')
      ],
      0
    );

    const mainSidesIndex = findOptionIndex(
      config.mainOptions,
      [
        (name) => name.includes('caras'),
        (name) => name.includes('cara'),
        (name) => name.includes('impresion')
      ],
      Math.min(1, Math.max(0, (config.mainOptions || []).length - 1))
    );

    const bindingSizeIndex = findOptionIndex(
      config.bindingOptions,
      [
        (name) => name.includes('tamano') && name.includes('papel'),
        (name) => name === 'tamano',
        (name) => name.includes('papel')
      ],
      0
    );

    const bindingCapacityIndex = findOptionIndex(
      config.bindingOptions,
      [
        (name) => name.includes('capacidad'),
        (name) => name.includes('hojas')
      ],
      Math.min(1, Math.max(0, (config.bindingOptions || []).length - 1))
    );

    let currentVariant = null;
    let busy = false;

    const getVariantById = (id) => {
      const numericId = Number(id);
      if (!numericId) return null;

      return (
        (config.mainVariants || []).find(
          (variant) => Number(variant.id) === numericId
        ) || null
      );
    };

    const getThemeVariantId = () => {
      const picker = section.querySelector?.(
        `hdt-variant-picker[form="${config.productFormId}"]`
      );

      const pickerVariantId = Number(picker?.getAttribute('selected-variant'));
      if (pickerVariantId) return pickerVariantId;

      const masterInput = mainForm.querySelector('input[name="id"]');
      const masterVariantId = Number(masterInput?.value);
      if (masterVariantId) return masterVariantId;

      return Number(config.initialVariantId) || 0;
    };

    const syncVariant = () => {
      currentVariant = getVariantById(getThemeVariantId());
      return currentVariant;
    };

    const wantsBinding = () =>
      bindingChoices.some(
        (input) => input.checked && input.value === 'yes'
      );

    const findBindingVariant = (paperSize, capacity) => {
      const wantedSize = normalize(paperSize);

      return (
        (config.bindingVariants || []).find((variant) => {
          const size = normalize(variant.options?.[bindingSizeIndex]);
          const variantCapacity = extractNumber(
            variant.options?.[bindingCapacityIndex]
          );

          return size === wantedSize && variantCapacity === capacity;
        }) || null
      );
    };

    const getCopies = () => positiveInteger(copiesInput.value) || 1;

    const normalizeBindingQuantity = (preferAll = false) => {
      const copies = getCopies();
      bindingQtyInput.max = String(copies);

      let current = positiveInteger(bindingQtyInput.value);

      if (preferAll || !current) {
        current = copies;
      }

      current = clamp(current, 1, copies);
      bindingQtyInput.value = String(current);

      if (bindingQtyHelp) {
        bindingQtyHelp.textContent =
          copies > 1
            ? `Puede anillar desde 1 hasta ${copies} ejemplares. Los demás se entregarán solo impresos.`
            : 'Este pedido tiene 1 ejemplar, por lo que se anillará ese único ejemplar.';
      }

      if (bindingMinus) {
        bindingMinus.disabled = current <= 1;
      }

      if (bindingPlus) {
        bindingPlus.disabled = current >= copies;
      }

      return current;
    };

    const updateBindingQuantityVisibility = (preferAll = false) => {
      const active = wantsBinding();

      if (bindingQtyWrap) {
        bindingQtyWrap.hidden = !active;
      }

      if (active) {
        normalizeBindingQuantity(preferAll);
      }

      return active;
    };

    const clearMessages = () => {
      if (errorBox) {
        errorBox.hidden = true;
        errorBox.textContent = '';
      }

      if (successBox) {
        successBox.hidden = true;
        successBox.textContent = '';
      }
    };

    const showError = (message, focusTarget = null, shouldScroll = false) => {
      if (!errorBox) return;

      if (successBox) {
        successBox.hidden = true;
        successBox.textContent = '';
      }

      errorBox.textContent = message;
      errorBox.hidden = false;

      if (shouldScroll) {
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      focusTarget?.focus?.();
    };

    const showSuccess = (message) => {
      if (!successBox) return;

      if (errorBox) {
        errorBox.hidden = true;
        errorBox.textContent = '';
      }

      successBox.textContent = message;
      successBox.hidden = false;
    };

    const getState = () => {
      const variant = currentVariant || syncVariant();
      const pages = positiveInteger(pagesInput.value);
      const copies = positiveInteger(copiesInput.value);
      const withBinding = wantsBinding();

      const paperSize = variant?.options?.[mainSizeIndex] || '';
      const sides = variant?.options?.[mainSidesIndex] || '';

      const sheetsPerCopy =
        pages > 0
          ? isDoubleSided(sides)
            ? Math.ceil(pages / 2)
            : pages
          : 0;

      const printQuantity =
        sheetsPerCopy > 0 && copies > 0 ? sheetsPerCopy * copies : 0;

      const capacity =
        withBinding && sheetsPerCopy > 0
          ? bindingCapacity(sheetsPerCopy)
          : 0;

      const selectedBindingVariant =
        withBinding && capacity > 0
          ? findBindingVariant(paperSize, capacity)
          : null;

      const bindingQuantity =
        withBinding && copies > 0
          ? clamp(positiveInteger(bindingQtyInput.value) || copies, 1, copies)
          : 0;

      const printSubtotal =
        variant && printQuantity > 0
          ? Number(variant.price || 0) * printQuantity
          : 0;

      const bindingSubtotal =
        selectedBindingVariant && bindingQuantity > 0
          ? Number(selectedBindingVariant.price || 0) * bindingQuantity
          : 0;

      return {
        variant,
        pages,
        copies,
        withBinding,
        paperSize,
        sides,
        sheetsPerCopy,
        printQuantity,
        capacity,
        bindingVariant: selectedBindingVariant,
        bindingQuantity,
        printSubtotal,
        bindingSubtotal,
        grandTotal: printSubtotal + bindingSubtotal
      };
    };

    const renderSummary = () => {
      const state = getState();

      clearMessages();
      updateBindingQuantityVisibility(false);

      if (!state.variant || !state.pages || !state.copies) {
        if (summary) summary.hidden = true;
        if (bindingSummary) bindingSummary.hidden = true;
        if (bindingTotalRow) bindingTotalRow.hidden = true;
        paintNativePrice(state);
        return state;
      }

      if (summary) summary.hidden = false;
      if (summarySize) summarySize.textContent = state.paperSize || '—';
      if (summarySides) summarySides.textContent = state.sides || '—';
      if (summarySheets) summarySheets.textContent = String(state.sheetsPerCopy);
      if (summaryPrintQty) summaryPrintQty.textContent = String(state.printQuantity);

      if (printSubtotalEl) {
        printSubtotalEl.textContent = formatMoney(state.printSubtotal);
      }

      if (grandTotalEl) {
        grandTotalEl.textContent = formatMoney(state.grandTotal);
      }

      if (!state.withBinding) {
        if (bindingSummary) bindingSummary.hidden = true;
        if (bindingTotalRow) bindingTotalRow.hidden = true;
        paintNativePrice(state);
        return state;
      }

      if (bindingSummary) bindingSummary.hidden = false;
      if (bindingTotalRow) bindingTotalRow.hidden = false;

      if (state.sheetsPerCopy > 240) {
        if (bindingTitle) bindingTitle.textContent = 'Anillado no disponible';

        if (bindingDetail) {
          bindingDetail.textContent =
            `Cada ejemplar requiere ${state.sheetsPerCopy} hojas. ` +
            'La capacidad máxima configurada es de 240 hojas.';
        }

        if (bindingSummarySubtotal) bindingSummarySubtotal.textContent = '—';
        if (bindingTotalEl) bindingTotalEl.textContent = '—';
        if (grandTotalEl) grandTotalEl.textContent = formatMoney(state.printSubtotal);

        paintNativePrice({
          ...state,
          grandTotal: state.printSubtotal
        });

        return state;
      }

      if (!state.bindingVariant) {
        if (bindingTitle) {
          bindingTitle.textContent = 'No se encontró una variante compatible';
        }

        if (bindingDetail) {
          bindingDetail.textContent =
            `${state.paperSize} · Hasta ${state.capacity} hojas`;
        }

        if (bindingSummarySubtotal) bindingSummarySubtotal.textContent = '—';
        if (bindingTotalEl) bindingTotalEl.textContent = '—';

        paintNativePrice({
          ...state,
          grandTotal: state.printSubtotal
        });

        return state;
      }

      if (bindingTitle) {
        bindingTitle.textContent =
          `${config.bindingTitle} · Hasta ${state.capacity} hojas`;
      }

      if (bindingDetail) {
        bindingDetail.textContent =
          `${state.bindingQuantity} de ${state.copies} ${
            state.copies === 1 ? 'ejemplar llevará' : 'ejemplares llevarán'
          } anillado · ${state.bindingVariant.priceFormatted} por unidad`;
      }

      if (bindingSummarySubtotal) {
        bindingSummarySubtotal.textContent = formatMoney(state.bindingSubtotal);
      }

      if (bindingTotalEl) {
        bindingTotalEl.textContent = formatMoney(state.bindingSubtotal);
      }

      if (grandTotalEl) {
        grandTotalEl.textContent = formatMoney(state.grandTotal);
      }

      paintNativePrice(state);

      return state;
    };

    const hideNativeQuantitySelectors = () => {
      const forms = [mainForm, stickyForm].filter(Boolean);

      forms.forEach((form) => {
        const formId = form.id;

        const quantityInputs = [
          ...(section.querySelectorAll?.('input[name="quantity"]') || [])
        ].filter(
          (input) =>
            input.form === form || input.getAttribute('form') === formId
        );

        quantityInputs.forEach((input) => {
          const wrapper =
            input.closest('.hdt-product-form__quantity') ||
            input.closest('.hdt-sticky-atc__qty-selector') ||
            input.closest('hdt-quantity-wrapp') ||
            input.parentElement;

          wrapper?.setAttribute('data-auros-native-qty-hidden', '');
        });
      });

      section
        .querySelectorAll?.('.hdt-sticky-atc__qty-selector')
        .forEach((element) => {
          element.setAttribute('data-auros-native-qty-hidden', '');
        });
    };

    const placeConfigurator = () => {
      const buyBlock =
        mainForm.closest('.hdt-product__buy-button') ||
        mainForm.closest('.hdt-product-info__item');

      if (buyBlock?.parentNode) {
        buyBlock.insertAdjacentElement('beforebegin', root);
      } else if (mainForm.parentNode) {
        mainForm.parentNode.insertBefore(root, mainForm);
      }

      root.hidden = false;
      root.classList.add('is-ready');

      hideNativeQuantitySelectors();
    };

    const collectExistingStringProperties = () => {
      const properties = {};
      const formData = new FormData(mainForm);

      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (!match) return;

        if (typeof value === 'string' && value.trim() !== '') {
          properties[match[1]] = value;
        }
      });

      return properties;
    };

    const validate = (state, submittedForm) => {
      if (
        typeof submittedForm?.checkValidity === 'function' &&
        !submittedForm.checkValidity()
      ) {
        submittedForm.reportValidity?.();
        return { valid: false, silent: true };
      }

      if (!state.variant) {
        return {
          valid: false,
          message: 'Seleccione el tamaño del papel y las caras impresas.'
        };
      }

      if (!state.variant.available) {
        return {
          valid: false,
          message: 'La variante de impresión seleccionada no está disponible.'
        };
      }

      if (!state.pages) {
        return {
          valid: false,
          message: 'Ingrese un número de páginas válido, mayor que cero.',
          focus: pagesInput
        };
      }

      if (!state.copies) {
        return {
          valid: false,
          message: 'Ingrese una cantidad de ejemplares válida, mayor que cero.',
          focus: copiesInput
        };
      }

      if (
        !Number.isSafeInteger(state.printQuantity) ||
        state.printQuantity <= 0
      ) {
        return {
          valid: false,
          message: 'La cantidad calculada de impresión no es válida.'
        };
      }

      const deferredPdfInput = getDeferredPdfInput();
      const deferredPdfRequired =
        mainForm.dataset.aurosPdfRequired === 'true' ||
        Boolean(deferredPdfInput);

      if (deferredPdfRequired) {
        const pdfFile = getDeferredPdfFile();

        if (!pdfFile) {
          return {
            valid: false,
            message:
              'Adjunte el archivo PDF que desea imprimir antes de añadir el pedido al carrito.'
          };
        }

        if (!isPdfFile(pdfFile)) {
          return {
            valid: false,
            message: 'El archivo adjunto debe estar en formato PDF.'
          };
        }

        if (pdfFile.size <= 0) {
          return {
            valid: false,
            message: 'El PDF adjunto está vacío. Seleccione un archivo válido.'
          };
        }

        if (pdfFile.size > MAXIMUM_PDF_SIZE) {
          return {
            valid: false,
            message: 'El PDF adjunto supera el límite máximo de 20 MB.'
          };
        }
      }

      if (state.withBinding && state.sheetsPerCopy > 240) {
        return {
          valid: false,
          message:
            `El documento requiere ${state.sheetsPerCopy} hojas por ejemplar y ` +
            'supera la capacidad máxima de anillado de 240 hojas. ' +
            'Seleccione “Solo impresión” para continuar.'
        };
      }

      if (state.withBinding && !state.bindingVariant) {
        return {
          valid: false,
          message:
            `No existe una variante de anillado compatible con ` +
            `${state.paperSize} y hasta ${state.capacity} hojas.`
        };
      }

      if (state.withBinding && !state.bindingVariant.available) {
        return {
          valid: false,
          message:
            `El anillado ${state.paperSize} de hasta ${state.capacity} hojas ` +
            'no está disponible actualmente.'
        };
      }

      if (
        state.withBinding &&
        (
          !state.bindingQuantity ||
          state.bindingQuantity < 1 ||
          state.bindingQuantity > state.copies
        )
      ) {
        return {
          valid: false,
          message:
            `Seleccione entre 1 y ${state.copies} ejemplares para anillar.`,
          focus: bindingQtyInput
        };
      }

      return { valid: true };
    };

    const setBusy = (enabled) => {
      busy = enabled;
      const pdfInput = getDeferredPdfInput();
      if (pdfInput) pdfInput.disabled = Boolean(enabled);

      [mainForm, stickyForm].filter(Boolean).forEach((form) => {
        form
          .querySelectorAll('button[type="submit"][name="add"]')
          .forEach((button) => {
            if (enabled) {
              button.dataset.aurosWasDisabled = button.disabled ? 'true' : 'false';
              button.disabled = true;
              button.setAttribute('aria-busy', 'true');
              return;
            }

            const wasDisabled = button.dataset.aurosWasDisabled === 'true';

            button.removeAttribute('aria-busy');

            if (!wasDisabled) {
              button.disabled = false;
            }

            delete button.dataset.aurosWasDisabled;
          });
      });
    };

    const refreshCartAfterAdd = async (result, previousCart, cartSectionId) => {
      const action =
        window.themeHDN?.settings?.actionAfterATC || 'open_cart_drawer';

      if (action === 'go_cart_page') {
        window.location.assign(`${localeRoot()}cart`);
        return;
      }

      if (action === 'go_checkout') {
        window.location.assign(`${localeRoot()}checkout`);
        return;
      }

      let activeCart = previousCart;

      if (cartSectionId && result?.sections?.[cartSectionId]) {
        const parsed = new DOMParser().parseFromString(
          result.sections[cartSectionId],
          'text/html'
        );

        const freshCart = parsed.querySelector('[ref="hdt-cart"]');

        if (freshCart && previousCart?.isConnected) {
          previousCart.replaceWith(freshCart);
          activeCart = freshCart;
        }
      }

      if (action === 'open_cart_drawer') {
        if (activeCart && typeof activeCart.open === 'function') {
          requestAnimationFrame(() => activeCart.open());
          return;
        }

        window.location.assign(`${localeRoot()}cart`);
      }
    };

    const readJsonResponse = async (response, fallbackMessage) => {
      let result;

      try {
        result = await response.json();
      } catch {
        throw new Error('Shopify devolvió una respuesta inválida.');
      }

      if (!response.ok || result?.status) {
        throw new Error(
          result?.description ||
            result?.message ||
            fallbackMessage
        );
      }

      return result;
    };

    const findAddedLine = (result, variantId, jobId) => {
      const lines = Array.isArray(result?.items)
        ? result.items
        : (Array.isArray(result) ? result : [result]);

      return (
        lines.find(
          (line) =>
            Number(line?.variant_id || line?.id) === Number(variantId) &&
            line?.properties?._auros_job_id === jobId
        ) ||
        lines.find(
          (line) =>
            Number(line?.variant_id || line?.id) === Number(variantId)
        ) ||
        lines.find((line) => Boolean(line?.key)) ||
        null
      );
    };

    const resolveParentLineKey = async (result, variantId, jobId) => {
      const addedLine = findAddedLine(result, variantId, jobId);

      if (addedLine?.key) {
        return addedLine.key;
      }

      const cartResponse = await fetch(`${localeRoot()}cart.js`, {
        headers: { Accept: 'application/json' }
      });

      const cart = await readJsonResponse(
        cartResponse,
        'No fue posible confirmar la línea principal del pedido.'
      );

      const cartLine = (cart.items || []).find(
        (line) =>
          Number(line?.variant_id || line?.id) === Number(variantId) &&
          line?.properties?._auros_job_id === jobId
      );

      return cartLine?.key || '';
    };

    const rollbackParentLine = async (lineKey) => {
      if (!lineKey) return;

      try {
        await fetch(`${localeRoot()}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0
          })
        });
      } catch (rollbackError) {
        console.error(
          '[AUROS Anillado] No fue posible revertir la línea principal:',
          rollbackError
        );
      }
    };

    const addConfiguredProduct = async (state) => {
      const jobId = createJobId();
      const properties = collectExistingStringProperties();
      const sourceFormData = new FormData(mainForm);
      const sellingPlan = sourceFormData.get('selling_plan');
      const pdfFile = getDeferredPdfFile();

      const parentProperties = {
        ...properties,
        'Páginas por ejemplar': String(state.pages),
        'Hojas físicas por ejemplar': String(state.sheetsPerCopy),
        'Ejemplares': String(state.copies),
        'Tamaño del papel': state.paperSize,
        'Caras impresas': state.sides,
        'Anillado': state.withBinding
          ? `Doble O - Hasta ${state.capacity} hojas (${state.bindingQuantity} de ${state.copies} ejemplares)`
          : 'Sin anillado',
        'Ejemplares con anillado': state.withBinding
          ? String(state.bindingQuantity)
          : '0',
        _auros_job_id: jobId
      };

      const previousCart = document.querySelector('[ref="hdt-cart"]');
      const cartSectionId =
        previousCart?.getAttribute('section-id') ||
        previousCart?.sectionId ||
        '';
      const sectionsUrl =
        `${window.location.pathname}${window.location.search}`;

      /*
       * La impresión se agrega primero mediante FormData. Esto permite que
       * Shopify reciba el PDF como propiedad de archivo de la misma línea.
       */
      const parentFormData = new FormData();
      parentFormData.set('id', String(state.variant.id));
      parentFormData.set('quantity', String(state.printQuantity));

      Object.entries(parentProperties).forEach(([key, value]) => {
        parentFormData.set(`properties[${key}]`, String(value));
      });

      if (pdfFile) {
        parentFormData.set(
          'properties[Archivo PDF para imprimir]',
          pdfFile,
          pdfFile.name
        );
      }

      if (sellingPlan) {
        parentFormData.set('selling_plan', String(sellingPlan));
      }

      /*
       * Si no hay anillado, esta primera petición es también la última y
       * solicita de una vez el HTML actualizado del carrito.
       */
      if (!state.withBinding && cartSectionId) {
        parentFormData.set('sections', cartSectionId);
        parentFormData.set('sections_url', sectionsUrl);
      }

      const parentResponse = await fetch(`${localeRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: parentFormData
      });

      const parentResult = await readJsonResponse(
        parentResponse,
        'No fue posible agregar la impresión al carrito.'
      );

      let finalResult = parentResult;
      let parentLineKey = '';

      if (state.withBinding) {
        parentLineKey = await resolveParentLineKey(
          parentResult,
          state.variant.id,
          jobId
        );

        if (!parentLineKey) {
          throw new Error(
            'La impresión se agregó, pero no fue posible relacionar el anillado. Retire la línea del carrito e inténtelo nuevamente.'
          );
        }

        const childPayload = {
          items: [
            {
              id: Number(state.bindingVariant.id),
              quantity: state.bindingQuantity,
              parent_line_key: parentLineKey,
              properties: {
                'Trabajo asociado': config.productTitle,
                'Ejemplares impresos': String(state.copies),
                'Ejemplares con anillado': String(state.bindingQuantity),
                'Tamaño del papel': state.paperSize,
                'Capacidad': `Hasta ${state.capacity} hojas`,
                'Hojas por ejemplar': String(state.sheetsPerCopy),
                _auros_job_id: jobId
              }
            }
          ]
        };

        if (cartSectionId) {
          childPayload.sections = [cartSectionId];
          childPayload.sections_url = sectionsUrl;
        }

        try {
          const childResponse = await fetch(`${localeRoot()}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify(childPayload)
          });

          finalResult = await readJsonResponse(
            childResponse,
            'No fue posible agregar el anillado al carrito.'
          );
        } catch (childError) {
          await rollbackParentLine(parentLineKey);
          throw childError;
        }
      }

      /*
       * Cuando existe PDF, la confirmación visual la maneja
       * auros-pdf-uploader.liquid con el mismo recuadro usado en
       * Impresión Color. Así evitamos dos mensajes de éxito distintos.
       */
      if (!pdfFile) {
        showSuccess(
          state.withBinding
            ? `Impresión y ${state.bindingQuantity} ${
                state.bindingQuantity === 1 ? 'anillado' : 'anillados'
              } agregados correctamente al carrito.`
            : 'Impresión agregada correctamente al carrito.'
        );
      }

      if (pdfFile) {
        document.dispatchEvent(
          new CustomEvent('auros:pdf-added-to-cart', {
            detail: {
              source: 'auros-anillado-automatico',
              fileName: pdfFile.name,
              jobId
            }
          })
        );
      }

      await refreshCartAfterAdd(finalResult, previousCart, cartSectionId);
    };

    const handleSubmit = async (event) => {
      event.preventDefault();

      const submittedForm = event.currentTarget;

      /*
       * Kalles revisa form.checkBreak al entrar a su manejador de submit.
       * Este listener trabaja en capture para evitar un segundo cart/add.js
       * durante el mismo evento.
       */
      submittedForm.checkBreak = true;

      const releaseNativeGuard = () => {
        queueMicrotask(() => {
          submittedForm.checkBreak = false;
        });
      };

      if (busy) {
        releaseNativeGuard();
        return;
      }

      clearMessages();

      setBusy(true);
      releaseNativeGuard();

      try {
        const pdfFile = getDeferredPdfFile();
        if (!pdfFile) {
          throw new Error('Adjunte un PDF para detectar las páginas antes de continuar.');
        }

        // Se espera la lectura iniciada al seleccionar el PDF; no hay un segundo cobro.
        const verifiedPages = await verifyPdfPages(pdfFile);
        if (getDeferredPdfFile() !== pdfFile) {
          throw new Error('El PDF cambió durante la verificación. Inténtelo de nuevo.');
        }

        pagesInput.value = String(verifiedPages);
        const state = renderSummary();
        const validation = validate(state, submittedForm);

        if (!validation.valid) {
          if (!validation.silent) {
            showError(
              validation.message,
              validation.focus || null,
              submittedForm === stickyForm
            );
          }
          return;
        }

        await addConfiguredProduct(state);
      } catch (error) {
        console.error('[AUROS Anillado] Error:', error);

        showError(
          error?.message ||
            'Ocurrió un error al agregar la configuración al carrito.'
        );
      } finally {
        setBusy(false);
      }
    };

    const onVariantChange = (event) => {
      const variantId = Number(event.detail?.variant?.id);

      currentVariant = getVariantById(variantId) || syncVariant();

      requestAnimationFrame(renderSummary);
    };

    placeConfigurator();
    syncVariant();
    updateBindingQuantityVisibility(false);
    renderSummary();

    document.addEventListener('change', onPdfFileChange, true);
    pagesInput.addEventListener('input', renderSummary);

    copiesInput.addEventListener('input', () => {
      normalizeBindingQuantity(false);
      renderSummary();
    });

    bindingChoices.forEach((input) => {
      input.addEventListener('change', () => {
        const justEnabled = input.checked && input.value === 'yes';
        updateBindingQuantityVisibility(justEnabled);
        renderSummary();
      });
    });

    bindingQtyInput.addEventListener('input', () => {
      normalizeBindingQuantity(false);
      renderSummary();
    });

    bindingQtyInput.addEventListener('change', () => {
      normalizeBindingQuantity(false);
      renderSummary();
    });

    bindingMinus?.addEventListener('click', () => {
      const current = normalizeBindingQuantity(false);
      bindingQtyInput.value = String(Math.max(1, current - 1));
      normalizeBindingQuantity(false);
      renderSummary();
    });

    bindingPlus?.addEventListener('click', () => {
      const copies = getCopies();
      const current = normalizeBindingQuantity(false);
      bindingQtyInput.value = String(Math.min(copies, current + 1));
      normalizeBindingQuantity(false);
      renderSummary();
    });

    mainForm.addEventListener('variant:change', onVariantChange);
    mainForm.addEventListener('submit', handleSubmit, true);

    if (stickyForm) {
      stickyForm.addEventListener('submit', handleSubmit, true);
    }

    const observer = new MutationObserver(hideNativeQuantitySelectors);

    if (section instanceof Element) {
      observer.observe(section, {
        childList: true,
        subtree: true
      });
    }

    if (window.Shopify?.designMode) {
      const unloadHandler = (event) => {
        if (
          event.target === section ||
          event.target?.contains?.(root)
        ) {
          observer.disconnect();
          document.removeEventListener(
            'shopify:section:unload',
            unloadHandler
          );
        }
      };

      document.addEventListener(
        'shopify:section:unload',
        unloadHandler
      );
    }
  }

  const initAll = (scope = document) => {
    const roots = [];

    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    }

    roots.forEach(init);
  };

  const boot = () => initAll(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();